/* eslint-disable sonarjs/cognitive-complexity */
import { errorToast } from '@/sagas/commonGenerators/errorToast';
import { ERROR_TOAST_DURATION } from '@/services/constants';
import { loadPaidNewsletter, loadSingleContact, savePaidNewsletter, updatePaidNewsletterStatus } from '@/services/ElasticApi';
import { getDialogStore, HideDialog } from '@/store/dialog/actions';
import { getMediaManagerStore, HideMediaManager } from '@/store/mediaManager/actions';
import {
    ContactStatus,
    EncodingType,
    Form,
    FormPageTypeEnum,
    FormPayload,
    FormSettingsEnum,
    LandingPagePayload,
    Newsletter,
    NewsletterPayload,
    NewsletterScopeEnum,
    NewsletterStatusEnum,
    Template,
    TemplateScope,
    TemplateType,
} from '@dev/ee-api/ee-api-models';
import { EditorSaveData, EditorSignUpField, EditorTemplateVersionBlocks, editorWebsiteBlocksMap } from '@dev/template-editor';
import { filter, find, forEach, includes, isEmpty, remove } from 'lodash-es';
import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import isEmail from 'validator/lib/isEmail';
import { push } from 'redux-first-history';
import { PaidNewslettersPaths } from '@/services/dictionaries/Paths';
import { getRecaptchaScript } from '@/services/tools/getRecaptchaScript';
import { deleteArrayForms, formsPagePreview, loadForm, saveForm } from '@/services/ApiCategories/forms';
import { generateLandingPageMetaTags } from '@/services/tools/generateLandingPageMetaTags';
import { checkIsUrlInvalid } from '@/services/tools/urlValidation/checkIsUrlInvalid';
import { safelyEncodeBase64Url, sendEventToGTM } from '@dev/ee-tools';
import {
    getPaidNewsletterCreatorStore,
    LoadError,
    LoadPageTemplateSuccess,
    LoadSuccess,
    SaveError,
    SaveSuccess,
    SetIsTemplateOpen,
    SetPaidNewsletterErrors,
    SetTemplatePreview,
    TemplateFormAddLoaded,
    TemplateFormAddNew,
    TemplateFormPayloadUpdate,
    TemplateFormSetErrors,
    TemplateFormsFromReplacedTemplateClear,
    TemplateFormsRemove,
    TemplateFormsSetInvalid,
    TemplateFormUpdateSaved,
    TemplateLoadSingleError,
    TemplateLoadSingleSuccess,
    SetEmailTemplateOpenType,
    SaveEmailTemplateSuccess,
    SaveEmailTemplateError,
    TemplateSetFromGallery,
    SaveSubscriptionCancelationTemplateSuccess,
} from '@/store/paidNewsletterCreator/actions';
import {
    EditorMetaData,
    EditorTemplateForm,
    PaidNewsletterCreatorActionTypes,
    PaidNewsletterCreatorState,
    PaidNewsletterCreatorTemplate,
    PaidNewsletterErrorNames,
    PaidNewsletterMetaData,
    LoadPageTemplateStartAction,
    LoadStartAction,
    SaveStartAction,
    TemplateFormErrorNames,
    TemplateFormsCheckInvalidAction,
    TemplateLoadSingleStartAction,
    TemplateOpenFromGalleryAction,
    TemplatePreviewFromGalleryAction,
    SaveEmailTemplateStartAction,
    PostPurchaseTemplatePayload,
    TemplatesSaveSettings,
    SubscriptionCancelationTemplatePayload,
    SaveSubscriptionCancelationTemplateStartAction,
} from '@/store/paidNewsletterCreator/types';
import { getMultiSignUpPublicFormIdDefaultValue } from '@/components/PaidNewsletterCreator/hooks/useMultiSignUpSettings';
import { getPaidNewsletterErrors } from '@/components/PaidNewsletterCreator/tools/getPaidNewsletterErrors';
import { saveLanding } from '@/services/ApiCategories/landings';
import { getShowMessagesScript } from '@/services/tools/getShowMessagesScript';
import { publishedPaidNewsletterNameQueryParam } from '@/components/PaidNewsletters/constants';
import { getPaymentTermsModalHtml, paymentTermsModalStyles } from '@/components/PaidNewsletterCreator/paymentTermsModalHtml';
import { CONFIRMATION_MULTI_TEMPLATE_INDEX, PAID_NEWSLETTER_CONFIRMATION_PAGE_PATH, PRODUCT_MULTI_TEMPLATE_INDEX } from '@/components/PaidNewsletterCreator/constants';
import i18n from '../../i18n';
import {
    loadTemplate as fetchTemplate,
    previewTemplate,
    updateTemplate,
} from '../services/ApiCategories/templates';
import { HideToast, ShowToast } from '../store/toast/actions';

const ShowTemplateErrorToast = (message: string, width = 485) =>
    ShowToast({
        severity: 'error',
        message,
        duration: ERROR_TOAST_DURATION,
        width,
        onClose: HideToast,
    });

const ShowTemplateInfoToast = (message: string, width = 485) =>
    ShowToast({
        severity: 'info',
        message,
        duration: ERROR_TOAST_DURATION,
        width,
        onClose: HideToast,
    });

function* onClose(): any {
    const { isOpened } = yield select(getDialogStore);
    const { isOpened: isMediaManagerOpened } = yield select(getMediaManagerStore);
    if (isOpened) {
        yield put(HideDialog());
    }
    if (isMediaManagerOpened) {
        yield put(HideMediaManager());
    }
}

function* load(action: LoadStartAction): any {
    const { payload } = action;
    const { name } = payload;

    try {
        const paidNewsletterObject: Newsletter = yield call(loadPaidNewsletter, name);
        const metaData: EditorMetaData | null = paidNewsletterObject.PaymentConfirmationLanding.Template.EditorMetaData
            ? JSON.parse(Buffer.from(paidNewsletterObject.PaymentConfirmationLanding.Template.EditorMetaData, 'base64').toString('utf8'))
            : null;
        yield all(
            (metaData?.data?.forms ?? [])?.map(({ blockClientId, formName, formMailingPreferencesLabel }) =>
                call(loadTemplateForm, blockClientId, formName, formMailingPreferencesLabel)
            )
        );
        yield put(LoadSuccess(paidNewsletterObject));
    } catch (e) {
        yield put(LoadError());
        yield call(errorToast, e);
    }
}

interface FormMultiBlockData {
    blockClientId: string;
    fieldNames?: string[];
    isRecaptchaRequired: boolean;
}

export const findFormsMulti = (blocks: EditorTemplateVersionBlocks): FormMultiBlockData[] => {
    let forms: FormMultiBlockData[] = [];
    blocks?.forEach((block) => {
        if (block.innerBlocks) {
            forms = [...forms, ...findFormsMulti(block.innerBlocks)];
        }
        if (editorWebsiteBlocksMap[block.name] !== 'formMulti') {
            return;
        }
        forms.push({
            blockClientId: block.clientId,
            fieldNames: (block.attributes?.fields as EditorSignUpField[])?.map(({ name }) => name),
            isRecaptchaRequired: !!block.attributes?.isRecaptchaRequired,
        });
    });
    return forms;
};

function* checkTemplateFormsByAction(action: TemplateFormsCheckInvalidAction): any {
    yield call(checkTemplateForms, action.payload.blockClientIds);
}

function* getContactStatus(email: string) {
    try {
        const contact = yield call(loadSingleContact, email);
        return contact.Status;
    } catch (e) {
        return 'Error';
    }
}

function* verifyEmailsToNotify(templateForm: EditorTemplateForm) {
    const errors: Partial<Record<TemplateFormErrorNames, boolean>> = {};
    const emailsToNotify = templateForm?.formPayload?.EmailsToNotifyOnAction?.split(';').map((item) => item.trim());
    const emailsNumberFromString = (templateForm?.formPayload?.EmailsToNotifyOnAction?.split('@').length ?? 0) - 1;

    if (!isEmpty(emailsToNotify)) {
        const emailsInvalid = !!find(emailsToNotify, (email) => !isEmail(email));
        if (emailsInvalid) {
            errors[TemplateFormErrorNames.NOTIFY_INVALID_EMAILS] = true;
        }
        if (emailsNumberFromString > 1 && emailsNumberFromString !== emailsToNotify.length) {
            errors[TemplateFormErrorNames.NOTIFY_EMAILS_NO_PROPER_SEPARATOR] = true;
        }
        if (!emailsInvalid && emailsToNotify.length > 5) {
            errors[TemplateFormErrorNames.NOTIFY_EMAILS_NUMBER_EXCEEDED] = true;
        }
        const recipientsStatuses = yield all(emailsToNotify.map((email) => call(getContactStatus, email)));
        const emailsStatusInvalid = !!find(
            recipientsStatuses,
            (status) => status !== ContactStatus.Active && status !== ContactStatus.Engaged
        );
        if (!emailsInvalid && !!emailsStatusInvalid) {
            errors[TemplateFormErrorNames.NOTIFY_EMAILS_NOT_ON_CONTACT_LIST] = true;
        }
    }

    if (!isEmpty(errors)) {
        yield put(TemplateFormSetErrors(errors, templateForm?.blockClientId));
    }

    return { errors };
}

function* verifyFormSubscriptionSettings(templateForm: EditorTemplateForm) {
    const errors: Partial<Record<TemplateFormErrorNames, boolean>> = {};
    const { blockClientId, formPayload } = templateForm;

    if (
        formPayload?.FormPageType?.[1] === FormPageTypeEnum.ExternalWebsiteFormSubmitPage &&
        (!formPayload?.FormSubmitPage || checkIsUrlInvalid(formPayload?.FormSubmitPage))
    ) {
        errors[TemplateFormErrorNames.FORM_SUBMIT_INVALID_URL] = true;
    }

    if (
        formPayload?.FormPageType?.[2] === FormPageTypeEnum.ExternalWebsiteFormSubscribedPage &&
        (!formPayload?.FormAlreadySubscribedPage || checkIsUrlInvalid(formPayload?.FormAlreadySubscribedPage))
    ) {
        errors[TemplateFormErrorNames.ALREADY_SUBSCRIBED_INVALID_URL] = true;
    }

    if (formPayload?.FormPageType?.[1] === FormPageTypeEnum.LandingPageFormSubmitPage && !formPayload?.FormSubmitPage) {
        errors[TemplateFormErrorNames.FORM_SUBMIT_NONE_LP] = true;
    }
    if (
        formPayload?.FormPageType?.[2] === FormPageTypeEnum.LandingPageFormSubscribedPage &&
        !formPayload?.FormAlreadySubscribedPage
    ) {
        errors[TemplateFormErrorNames.ALREADY_SUBSCRIBED_NONE_LP] = true;
    }

    yield put(TemplateFormSetErrors(errors, blockClientId));

    return { errors };
}

function* isTemplateFormInvalid(templateForm: EditorTemplateForm): any {
    const { formPayload, form } = templateForm;
    if (!templateForm || templateForm.isDeleted === true) {
        return true;
    }
    if (!templateForm?.formPayload?.Audience?.length) {
        // AT LEAST ONE CONTACT LIST IS REQUIRED
        return true;
    }

    const checkFormSettingsFieldsExists = (formPayload: FormPayload, formObject: Form): boolean => {
        const formAlreadySubscribedPage =
            formPayload?.FormAlreadySubscribedPage ?? formObject?.FormAlreadySubscribedPage;
        const formSubmitPage = formPayload?.FormSubmitPage ?? formObject?.FormSubmitPage;

        return typeof formAlreadySubscribedPage !== 'undefined' && typeof formSubmitPage !== 'undefined';
    };

    //subscription section validation
    if (checkFormSettingsFieldsExists(formPayload, form)) {
        const { errors } = yield call(verifyFormSubscriptionSettings, templateForm);
        if (!isEmpty(errors)) return true;
    }

    // Notifications section validation
    if (includes(templateForm?.formPayload?.FormSettings, FormSettingsEnum.NotifyEmails)) {
        const { errors } = yield call(verifyEmailsToNotify, templateForm);
        if (!isEmpty(errors)) return true;
    }
}

function* getFormValidity(forms: EditorTemplateForm[], clientId: string) {
    const matchedTemplateForm = forms?.find((templateForm) => templateForm.blockClientId === clientId);
    const isInvalid = yield call(isTemplateFormInvalid, matchedTemplateForm);
    if (!isInvalid) return;
    return clientId;
}

function* checkTemplateForms(formsMultiIds?: string[], suppressErrors?: boolean): any {
    const {
        template,
    }: {
        template: Partial<PaidNewsletterCreatorTemplate>;
    } = yield select(getPaidNewsletterCreatorStore);
    const formsMulti =
        formsMultiIds ?? template.forms?.filter((form) => !form.isDeleted).map((form) => form.blockClientId);

    const formsValidity = yield all(formsMulti.map((clientId) => call(getFormValidity, template.forms, clientId)));
    const invalidForms = filter(formsValidity);

    const isSaveBlocked = !!invalidForms.length && !suppressErrors;
    yield put(TemplateFormsSetInvalid(isSaveBlocked ? invalidForms : []));
    return isSaveBlocked;
}

const getFormSettingsWithRecaptcha = (
    isRecaptchaRequired: boolean,
    formSettings: FormSettingsEnum[]
): FormSettingsEnum[] => {
    if (isRecaptchaRequired && !formSettings.length) {
        return [FormSettingsEnum.RequireReCaptcha];
    }
    const isRecaptchaInSettings = includes(formSettings, FormSettingsEnum.RequireReCaptcha);
    if (isRecaptchaRequired && !isRecaptchaInSettings) {
        return [...formSettings, FormSettingsEnum.RequireReCaptcha];
    }
    if (!isRecaptchaRequired && isRecaptchaInSettings) {
        return remove([...formSettings], (setting) => setting !== FormSettingsEnum.RequireReCaptcha);
    }
    return [...formSettings];
};

interface SavedForm {
    blockClientId: string,
    publicformid: string,
    isNew: boolean
}

function* saveTemplateForms(data?: Partial<EditorSaveData>, exitOnSave?: boolean, suppressErrors?: boolean): any {
    if (!data) {
        return { isSaveBlocked: false };
    }
    const {
        template,
    }: {
        template: Partial<PaidNewsletterCreatorTemplate>;
    } = yield select(getPaidNewsletterCreatorStore);
    const alreadyHadError = !!template.forms?.find((form) => !form.isDeleted && form.isError);

    const paymentForms = findFormsMulti(data?.multiTemplates?.[1]?.contentJson?.blocks) ?? []; // search in the payment template, should find only one (in the default template)
    const formsMulti = findFormsMulti(data?.multiTemplates?.[2]?.contentJson?.blocks) ?? []; // search in the confirmation template

    const paymentFormFieldNames = paymentForms?.[0]?.fieldNames?.join(',');

    const formsToCreate = formsMulti.filter(({ blockClientId }) => {
        const matchedTemplateForm = template.forms?.find(
            (templateForm) => templateForm.blockClientId === blockClientId
        );
        return !matchedTemplateForm || matchedTemplateForm.isDeleted === true;
    });
    const formsToRemove: string[] = [];
    const formsToRemoveBlockClientIds: string[] = [];
    forEach(template?.forms ?? [], (existingForm) => {
        if (
            !existingForm.isDeleted &&
            !formsMulti.find(({ blockClientId }) => blockClientId === existingForm.blockClientId)
        ) {
            if (existingForm.form) {
                formsToRemove.push(existingForm.form.Name);
            }
            formsToRemoveBlockClientIds.push(existingForm.blockClientId);
        }
    });

    if (template.formsFromReplacedTemplate?.length) {
        yield call(deleteArrayForms, template.formsFromReplacedTemplate.map(templateForm => templateForm.form.Name));
        yield put(TemplateFormsFromReplacedTemplateClear());
    }

    if (formsToRemove.length) {
        yield call(deleteArrayForms, formsToRemove);
        yield put(TemplateFormsRemove(formsToRemoveBlockClientIds));
    }

    yield all(formsToCreate.map(({ blockClientId }) => put(TemplateFormAddNew(blockClientId))));
    const isSaveBlocked = yield call(
        checkTemplateForms,
        formsMulti.map(({ blockClientId }) => blockClientId),
        suppressErrors
    );
    yield all(
        formsMulti.map(({ blockClientId, fieldNames, isRecaptchaRequired }) => {
            const templateForm = template?.forms?.find(
                ({ blockClientId: templateFormBlockClientId }) => blockClientId === templateFormBlockClientId
            );
            const formSettings = templateForm?.formPayload?.FormSettings ?? templateForm?.form?.FormSettings ?? [];
            const newFormSettings = getFormSettingsWithRecaptcha(isRecaptchaRequired, formSettings);
            return put(
                TemplateFormPayloadUpdate(blockClientId, {
                    MergeFields: fieldNames?.join(','),
                    FormSettings: newFormSettings,
                })
            );
        })
    );

    const {
        template: updatedTemplates,
    }: {
        template: Partial<PaidNewsletterCreatorTemplate>;
    } = yield select(getPaidNewsletterCreatorStore);

    if (isSaveBlocked && !suppressErrors) {
        if (exitOnSave) {
            yield put(ShowTemplateErrorToast(i18n.t('fixerrorstocontinue'), 336));
        } else if (!alreadyHadError) {
            yield put(ShowTemplateInfoToast(i18n.t('fixerrorstoenableautosave'), 386));
        }
        return { isSaveBlocked: true };
    }
    let savedFormsPublicIds;
    if (updatedTemplates.forms) {
        savedFormsPublicIds = yield all(
            filterUndeletedEditorTemplateForm(updatedTemplates.forms).map((item) =>
                call(saveTemplateForm, item.blockClientId, item.formPayload, item?.form?.Name)
            )
        );
    }
    savedFormsPublicIds = savedFormsPublicIds?.filter(savedForm => savedForm.isNew); //FILTER SAVED TO CREATED ONLY
    return { isSaveBlocked: false, savedFormsPublicIds, paymentFormFieldNames };
}

function* saveTemplateForm(blockClientId: string, formPayload: FormPayload, name?: string): any {
    const response = yield call(saveForm, formPayload, name);
    yield put(TemplateFormUpdateSaved(blockClientId, response));
    return { blockClientId, publicformid: response.PublicFormID, isNew: !name };
}

function* loadTemplateForm(blockClientId: string, name: string, formMailingPreferencesLabel?: string): any {
    const response = yield call(loadForm, name);
    yield put(TemplateFormAddLoaded(blockClientId, response, formMailingPreferencesLabel));
}

const filterUndeletedEditorTemplateForm = (forms?: EditorTemplateForm[]): EditorTemplateForm[] =>
    forms?.filter((form) => !form.isDeleted) ?? [];

interface GetTemplateToSaveProps {
    name: string, 
    multiTemplateIndex: number, 
    templatesData?: EditorSaveData, 
    savedForms?: SavedForm[],
    templateType: TemplateType
}

function* getTemplateToSave({name, multiTemplateIndex, templatesData, savedForms, templateType }: GetTemplateToSaveProps): any {
    const {
        templateObject,
        template,
        templatesSaveSettings
    }: {
        templateObject: Partial<Template>;
        template: Partial<PaidNewsletterCreatorTemplate>;
        templatesSaveSettings: Partial<TemplatesSaveSettings>;
    } = yield select(getPaidNewsletterCreatorStore);

    const isProductTemplate = multiTemplateIndex === PRODUCT_MULTI_TEMPLATE_INDEX;
    const isConfirmationTemplate = multiTemplateIndex === CONFIRMATION_MULTI_TEMPLATE_INDEX;

    const templateData = templatesData?.multiTemplates?.[multiTemplateIndex];

    const metaDataPayload: EditorMetaData = {
        data: {
            contentJson: templateData?.contentJson,
            recentColors: template.recentColors ?? [],
            forms: isConfirmationTemplate ? filterUndeletedEditorTemplateForm(template.forms).map((form) => ({
                blockClientId: form.blockClientId,
                formName: form.formPayload.Name,
                formMailingPreferencesLabel: form.formMailingPreferencesLabel,
            })) : null,
        },
    };
    let content = templatesData?.multiTemplates[multiTemplateIndex].content;
    if (isConfirmationTemplate && content && savedForms?.length) {
        savedForms.forEach((savedForm) => {
            content = content.replace(getMultiSignUpPublicFormIdDefaultValue(savedForm.blockClientId), savedForm.publicformid);
        })
    }
    content = content.replace('</body>',getPaymentTermsModalHtml());
    
    const screenshot = templatesSaveSettings?.customScreenshot || templatesData?.screenshot;
    return {
        ...templateObject,
        TemplateType: templateType,
        Name: name,
        ...(isProductTemplate && screenshot ? { Base64Image: screenshot } : null),
        EditorMetaData: templateData?.contentJson
            ? Buffer.from(JSON.stringify(metaDataPayload)).toString('base64')
            : templateObject?.EditorMetaData,
        TemplateScope: templatesSaveSettings?.isPublic ? TemplateScope.Public : TemplateScope.Private,
        IsDefault: templatesSaveSettings?.isDefault ?? false,
        Body: {
            Parts: [
                ...(content
                    ? [
                          {
                              Charset: null,
                              Content: content,
                              ContentType: 'text/html',
                              Encoding: EncodingType.None,
                          },
                      ]
                    : []),
            ],
        },
    };
}

const getPaidNewsletterMetadata = (
    paidNewsletterMetaDataPayload: PaidNewsletterMetaData,
    paidNewsletterMetaDataObject: PaidNewsletterMetaData
): PaidNewsletterMetaData => {
    if (!paidNewsletterMetaDataPayload && !paidNewsletterMetaDataObject) {
        // it should not be saved before
        return null;
    }
    const payload: PaidNewsletterMetaData = {
        data: {
            socialPreview: {
                imageUrl: paidNewsletterMetaDataObject?.data?.socialPreview?.imageUrl ?? '',
                pageTitle: paidNewsletterMetaDataObject?.data?.socialPreview?.pageTitle ?? '',
                pageDescription: paidNewsletterMetaDataObject?.data?.socialPreview?.pageDescription ?? '',
                ...paidNewsletterMetaDataPayload?.data?.socialPreview,
            },
        },
    };
    return payload;
};

function* saveLandingsTemplates(templatesData?: EditorSaveData, savedForms?: SavedForm[]): any {
    if (!templatesData) {
        return;
    }
    const { 
        paidNewsletterObject, 
        templatesSaveSettings 
    }: { 
        paidNewsletterObject: Partial<Newsletter>;
        templatesSaveSettings: Partial<TemplatesSaveSettings>; 
    } = yield select(getPaidNewsletterCreatorStore);
    const productTemplateName = paidNewsletterObject?.ProductLanding?.Template?.Name;
    const productTemplateSaveName = templatesSaveSettings?.isPublic ? paidNewsletterObject?.Name : productTemplateName;
    const confirmationTemplateName = paidNewsletterObject?.PaymentConfirmationLanding?.Template?.Name;

    if (!productTemplateName || !confirmationTemplateName) {
        return;
    }

    const productTemplateToSave: Template = yield call(getTemplateToSave, { name: productTemplateSaveName, multiTemplateIndex: PRODUCT_MULTI_TEMPLATE_INDEX, templatesData, templateType: TemplateType.LandingPageNewsletterProductTemplate });
    const confirmationTemplateToSave: Template = yield call(getTemplateToSave, { name: confirmationTemplateName, multiTemplateIndex: CONFIRMATION_MULTI_TEMPLATE_INDEX, templatesData, templateType: TemplateType.LandingPageNewsletterPaymentConfirmationTemplate, savedForms });

    yield all([
        call(updateTemplate, productTemplateName, productTemplateToSave),
        call(updateTemplate, confirmationTemplateName, confirmationTemplateToSave)
    ]);

    if (productTemplateName !== productTemplateSaveName) {
        return productTemplateSaveName;
    }
}

function* save({ payload, meta }: SaveStartAction): any {
    const { data: templatesData, exitOnSave, errors, errorsToCheck } = payload || {};
    //SAVE FORMS BEFORE LANDING PAGE
    const { isSaveBlocked, savedFormsPublicIds, paymentFormFieldNames } = yield call(saveTemplateForms, templatesData, exitOnSave);

    if (isSaveBlocked) {
        yield put(SaveError(meta));
        return;
    }

    try {
        if (errors) {
            throw errors;
        }

        const {
            paidNewsletterObject,
            paidNewsletterPayload,
            template,
            paidNewsletterMetaDataObject,
            paidNewsletterMetaDataPayload,
            postPurchaseTemplatePayload,
            subscriptionCancelationTemplatePayload,
            urlPayload,
            templatesSaveSettings
        }: {
            paidNewsletterObject: Partial<Newsletter>;
            paidNewsletterPayload: Partial<NewsletterPayload>;
            template: Partial<PaidNewsletterCreatorTemplate>;
            paidNewsletterMetaDataObject: PaidNewsletterMetaData;
            paidNewsletterMetaDataPayload: PaidNewsletterMetaData;
            postPurchaseTemplatePayload?: PostPurchaseTemplatePayload;
            subscriptionCancelationTemplatePayload?: SubscriptionCancelationTemplatePayload;
            urlPayload?: string;
            templatesSaveSettings: Partial<TemplatesSaveSettings>;
        } = yield select(getPaidNewsletterCreatorStore);
        const currentPaidNewsletterName = paidNewsletterPayload?.Name ?? paidNewsletterObject?.Name;

        const paidNewsletterToSave: NewsletterPayload = {
            Name: currentPaidNewsletterName,
            Currency: paidNewsletterPayload?.Currency ?? paidNewsletterObject?.Currency,
            RecurringInterval: paidNewsletterPayload?.RecurringInterval ?? paidNewsletterObject?.RecurringInterval,
            ProductPrice: paidNewsletterPayload?.ProductPrice ?? paidNewsletterObject?.ProductPrice,
            MergeFields: paymentFormFieldNames ?? paidNewsletterObject?.MergeFields,
            ...(templatesSaveSettings?.isPublic ? { Scope: NewsletterScopeEnum.Public } : null) // public paidNewsletter to show in gallery
        };

        const paidNewsletterErrors = getPaidNewsletterErrors(paidNewsletterToSave, errorsToCheck);
        if (!templatesData && Object.keys(paidNewsletterErrors).length) { //do not force errors when save is triggered from template editor
            yield put(SetPaidNewsletterErrors(paidNewsletterErrors));
            yield put(SaveError());
            return;
        }

        const formsUsed = filterUndeletedEditorTemplateForm(template?.forms);
        
        const isRecaptchaUsed = formsUsed?.find((templateForm) =>
            templateForm.form?.FormSettings?.includes(FormSettingsEnum.RequireReCaptcha)
        );
        

        const productTemplateSaveName = yield call(saveLandingsTemplates, templatesData, savedFormsPublicIds);


        const paidNewsletterMetaData = paidNewsletterMetaDataPayload || paidNewsletterMetaDataObject
            ? getPaidNewsletterMetadata(paidNewsletterMetaDataPayload, paidNewsletterMetaDataObject)
            : null;

        if (paidNewsletterObject?.ProductLanding?.Name) {
            const metaHtml = [
                paymentTermsModalStyles,
                `<title>${paidNewsletterObject.Name}</title>`,
                `<base href="{currenturl}/">`,
                paidNewsletterMetaData ? generateLandingPageMetaTags(paidNewsletterMetaData) : '',
                isRecaptchaUsed ? getRecaptchaScript() : '',
                getShowMessagesScript(),
            ].join('');

            const productLandingToSave: LandingPagePayload = {
                FormNames: formsUsed?.map((item) => item.form.Name).join(','),
                LandingMetaJson: paidNewsletterMetaData
                    ? Buffer.from(JSON.stringify(paidNewsletterMetaData)).toString('base64')
                    : null,
                LandingMetaHtml: metaHtml,
                ...(productTemplateSaveName ? { LandingTemplateName: productTemplateSaveName } : null)
            };

            yield call(
                saveLanding,
                productLandingToSave,
                paidNewsletterObject?.ProductLanding?.Name
            );
        }

        if (postPurchaseTemplatePayload) {
            const emailTemplatePayload: Template = {
                ...paidNewsletterObject.PostPurchaseTemplate,
                ...(postPurchaseTemplatePayload.fromEmail ? { FromEmail: postPurchaseTemplatePayload.fromEmail } : null), 
                ...(postPurchaseTemplatePayload.fromName ? { FromName: postPurchaseTemplatePayload.fromName } : null), 
                Body: {
                    ...paidNewsletterObject.PostPurchaseTemplate.Body,
                    ...(postPurchaseTemplatePayload.subject ? { Subject: postPurchaseTemplatePayload.subject } : null),
                },
            }
            yield call(updateTemplate, paidNewsletterObject.PostPurchaseTemplate?.Name, emailTemplatePayload);
        }
        
        if (subscriptionCancelationTemplatePayload) {
            const cancelationTemplatePayload: Template = {
                ...paidNewsletterObject.CancellationTemplate,
                ...(subscriptionCancelationTemplatePayload.fromEmail ? { FromEmail: subscriptionCancelationTemplatePayload.fromEmail } : null), 
                ...(subscriptionCancelationTemplatePayload.fromName ? { FromName: subscriptionCancelationTemplatePayload.fromName } : null), 
                Body: {
                    ...paidNewsletterObject.CancellationTemplate.Body,
                    ...(subscriptionCancelationTemplatePayload.subject ? { Subject: subscriptionCancelationTemplatePayload.subject } : null),
                },
            }
            yield call(updateTemplate, paidNewsletterObject.CancellationTemplate?.Name, cancelationTemplatePayload);
        }

        const savedPaidNewsletter = yield call(
            savePaidNewsletter,
            paidNewsletterToSave,
            paidNewsletterObject?.Name
        );

        yield put(SaveSuccess(savedPaidNewsletter, templatesData, paidNewsletterMetaData, meta));
        if (exitOnSave) {
            yield put(SetIsTemplateOpen(false));
        }
    } catch (e) {
        if (e?.response?.data?.MessageID === 'template_from_not_associated_with_account') {
            yield put(SetPaidNewsletterErrors({ [PaidNewsletterErrorNames.INVALID_FROM_EMAIL]: true }));
            return;
        }
        if (e?.response?.data?.MessageID === 'newsletter_name_in_use') {
            yield put(SetPaidNewsletterErrors({ [PaidNewsletterErrorNames.DUPLICATED_NAME]: true }));
            return;
        }
        yield put(SaveError());
        yield call(errorToast, e);
    }
}

function* saveEmailTemplate({ payload, meta }: SaveEmailTemplateStartAction): any {
    const { data: templateData, exitOnSave } = payload || {};

    try {
        const { 
            paidNewsletterObject,
            templatesSaveSettings
        }: {
            paidNewsletterObject: Partial<Newsletter>;
            templatesSaveSettings: Partial<TemplatesSaveSettings>;
        } = yield select(getPaidNewsletterCreatorStore);


        const emailTemplatePayload = {
            ...paidNewsletterObject.PostPurchaseTemplate,
            ...(templateData?.screenshot ? { Base64Image: templateData.screenshot } : null),
            EditorMetaData: Buffer.from(JSON.stringify({data: { contentJson: templateData?.contentJson, recentColors: templateData?.recentColors }})).toString('base64'),
            Body: {
                ...paidNewsletterObject.PostPurchaseTemplate.Body,
                Parts: [
                    {
                        Charset: null,
                        Content: templateData.content,
                        ContentType: 'text/html',
                        Encoding: EncodingType.None,
                    },
                ]
            },
            ...(templatesSaveSettings?.isEmailDefault && { TemplateScope: TemplateScope.Public, IsDefault: true }),
        }
        const savedEmailTemplate = yield call(updateTemplate, paidNewsletterObject.PostPurchaseTemplate?.Name, emailTemplatePayload);
        yield put(SaveEmailTemplateSuccess(templateData, savedEmailTemplate, meta));

        if (exitOnSave) {
            yield put(SetEmailTemplateOpenType(null));
        }
        
    } catch (e) {
        yield put(SaveEmailTemplateError());
        yield call(errorToast, e);
    }
}

function* saveSubscriptionCancelationTemplate({ payload, meta }: SaveSubscriptionCancelationTemplateStartAction): any {
    const { data: templateData, exitOnSave } = payload || {};

    try {
        const { 
            paidNewsletterObject,
            templatesSaveSettings
        }: {
            paidNewsletterObject: Partial<Newsletter>;
            templatesSaveSettings: Partial<TemplatesSaveSettings>;
        } = yield select(getPaidNewsletterCreatorStore);


        const emailTemplatePayload = {
            ...paidNewsletterObject.CancellationTemplate,
            ...(templateData?.screenshot ? { Base64Image: templateData.screenshot } : null),
            EditorMetaData: Buffer.from(JSON.stringify({data: { contentJson: templateData?.contentJson, recentColors: templateData?.recentColors }})).toString('base64'),
            Body: {
                ...paidNewsletterObject.CancellationTemplate.Body,
                Parts: [
                    {
                        Charset: null,
                        Content: templateData.content,
                        ContentType: 'text/html',
                        Encoding: EncodingType.None,
                    },
                ]
            },
            ...(templatesSaveSettings?.isEmailDefault && { TemplateScope: TemplateScope.Public, IsDefault: true }),
        }
        const savedEmailTemplate = yield call(updateTemplate, paidNewsletterObject.CancellationTemplate?.Name, emailTemplatePayload);
        yield put(SaveSubscriptionCancelationTemplateSuccess(templateData, savedEmailTemplate, meta));

        if (exitOnSave) {
            yield put(SetEmailTemplateOpenType(null));
        }
        
    } catch (e) {
        yield put(SaveEmailTemplateError());
        yield call(errorToast, e);
    }
}

function* loadTemplateSingle({ payload, meta }: TemplateLoadSingleStartAction): any {
    const { templateName, isPublic } = payload;
    if (!templateName) {
        yield put(TemplateLoadSingleError(meta));
        return;
    }
    const template: Template = yield call(fetchTemplate, templateName, isPublic);
    if (!template || !template?.Screenshot) {
        yield put(TemplateLoadSingleError(meta));
        return;
    }
    yield put(TemplateLoadSingleSuccess(template, meta));
}

function* openTemplateFromGallery(action: TemplateOpenFromGalleryAction): any {
    const { productTemplateName, confirmationTemplateName,isPublic } = action.payload;
    try {
        const [productTemplate, confirmationTemplate] = yield all([
            call(fetchTemplate, productTemplateName, isPublic, TemplateType.LandingPageProductTemplate),
            call(fetchTemplate, confirmationTemplateName, isPublic, TemplateType.LandingPageProductTemplate)
        ]);
        
        yield put(TemplateSetFromGallery(productTemplate, confirmationTemplate));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* previewTemplateFromGallery(action: TemplatePreviewFromGalleryAction): any {
    const { payload } = action;
    try {
        const templateContent = yield call(previewTemplate, payload.templateName, payload.isPublic,payload?.templateType);
        if (!templateContent) {
            return;
        }
        yield put(SetTemplatePreview(templateContent, payload.templateName, payload.isPublic));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* loadPageTemplateSaga({ payload }: LoadPageTemplateStartAction): any {
    const { pageTemplateType } = payload;
    try {
        const pageTemplate = yield call(formsPagePreview, pageTemplateType);
        yield put(LoadPageTemplateSuccess(pageTemplateType, pageTemplate?.data));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* showSaveErrors(): any {
    const { template }: PaidNewsletterCreatorState = yield select(getPaidNewsletterCreatorStore);
    const blocksValidationErrors = template.forms?.filter((form) => !form.isDeleted && form.isError);
    if (!blocksValidationErrors.length) {
        return;
    }
    yield put(ShowTemplateErrorToast(i18n.t('fixerrorstocontinue'), 336));
}

function* publishPaidNewsletter(): any {
    const { paidNewsletterObject }: PaidNewsletterCreatorState = yield select(getPaidNewsletterCreatorStore);
    try {
        sendEventToGTM("paid_newsletter_published");
        yield call(updatePaidNewsletterStatus, paidNewsletterObject.Name, NewsletterStatusEnum.Published);
        yield put(push(`${PaidNewslettersPaths.PaidNewslettersList}?${publishedPaidNewsletterNameQueryParam}=${safelyEncodeBase64Url(paidNewsletterObject.Name)}`));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* watchLoadStart(): any {
    yield takeEvery(PaidNewsletterCreatorActionTypes.LOAD_START, load);
    yield takeEvery(PaidNewsletterCreatorActionTypes.SAVE_START, save);
    yield takeEvery(PaidNewsletterCreatorActionTypes.SAVE_EMAIL_TEMPLATE_START, saveEmailTemplate);
    yield takeEvery(PaidNewsletterCreatorActionTypes.SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_START, saveSubscriptionCancelationTemplate);
    yield takeLatest(PaidNewsletterCreatorActionTypes.CLOSE, onClose);
    yield takeEvery(PaidNewsletterCreatorActionTypes.TEMPLATE_LOAD_SINGLE_START, loadTemplateSingle);
    yield takeLatest(PaidNewsletterCreatorActionTypes.TEMPLATE_OPEN_FROM_GALLERY, openTemplateFromGallery);
    yield takeLatest(PaidNewsletterCreatorActionTypes.TEMPLATE_PREVIEW_FROM_GALLERY, previewTemplateFromGallery);
    yield takeLatest(PaidNewsletterCreatorActionTypes.TEMPLATE_FORMS_CHECK_INVALID, checkTemplateFormsByAction);
    yield takeEvery(PaidNewsletterCreatorActionTypes.LOAD_PAGE_TEMPLATE_START, loadPageTemplateSaga);
    yield takeLatest(PaidNewsletterCreatorActionTypes.TEMPLATE_SHOW_SAVE_ERRORS, showSaveErrors);
    yield takeLatest(PaidNewsletterCreatorActionTypes.PUBLISH_PAID_NEWSLETTER_START, publishPaidNewsletter);
}

const paidNewsletterCreatorSagas = [fork(watchLoadStart)];

export default paidNewsletterCreatorSagas;
